"use client";

import React from 'react';

interface ComparisonItem {
  metric: string;
  before: string;
  after: string;
  improvement: string;
}

const comparisonData: ComparisonItem[] = [
  {
    metric: 'Batch Recall Response',
    before: '48-72 hours',
    after: '2-4 hours',
    improvement: '90% faster'
  },
  {
    metric: 'Expiry Losses',
    before: '3-5% of inventory',
    after: '0.5-1% of inventory',
    improvement: '70-80% reduction'
  },
  {
    metric: 'Audit Preparation Time',
    before: '3-4 weeks',
    after: '2-3 days',
    improvement: '85% faster'
  },
  {
    metric: 'Regulatory Reporting',
    before: 'Manual, 5-7 days',
    after: 'Automated, real-time',
    improvement: 'Instant access'
  },
  {
    metric: 'Distributor Visibility',
    before: 'Monthly reports',
    after: 'Real-time dashboards',
    improvement: 'Live insights'
  },
  {
    metric: 'Manual Reporting Work',
    before: '40+ hours/week',
    after: '5-8 hours/week',
    improvement: '80% reduction'
  }
];

export default function FinancialImpactSection() {
  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-white px-4 sm:px-6 lg:px-8 xl:px-12 w-full md:min-h-screen md:flex md:items-center">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-navy text-2xl sm:text-3xl md:text-4xl xl:text-[3rem] leading-[1.2] font-bold mb-6">
            What Pharma Data Chaos <span className="italic">Costs You</span>
          </h2>
          <p className="text-charcoal text-lg leading-[1.7] max-w-3xl mx-auto">
            Quantifiable improvements after implementing an Enterprise Data Warehouse for pharmaceutical operations.
          </p>
        </div>

        {/* Before/After Comparison Table */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="bg-cream rounded-2xl p-8 md:p-10 border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-navy">
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">Metric</th>
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">Legacy Approach</th>
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">Simax EDW</th>
                    <th className="text-left py-4 px-4 font-serif text-navy text-lg font-bold">Improvement</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-white/50 transition-colors">
                      <td className="py-4 px-4 text-charcoal font-medium">{item.metric}</td>
                      <td className="py-4 px-4 text-red-600">{item.before}</td>
                      <td className="py-4 px-4 text-green-600 font-medium">{item.after}</td>
                      <td className="py-4 px-4">
                        <span className="inline-block bg-gold/10 text-gold text-xs font-bold uppercase tracking-wider px-3 py-1 rounded">
                          {item.improvement}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ROI Box */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gold to-gold/80 rounded-2xl p-8 md:p-10 shadow-xl">
            <div className="text-center mb-6">
              <h3 className="font-serif text-navy text-3xl md:text-4xl font-bold mb-4">
                Typical Financial Impact
              </h3>
              <p className="text-navy/80 text-lg leading-[1.6]">
                For a mid-sized pharmaceutical company with €500M+ annual revenue:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white/90 rounded-xl p-6 text-center">
                <div className="text-navy text-3xl font-bold mb-2">€2-4M</div>
                <div className="text-charcoal text-sm font-medium">Annual Savings</div>
              </div>
              <div className="bg-white/90 rounded-xl p-6 text-center">
                <div className="text-navy text-3xl font-bold mb-2">6-12</div>
                <div className="text-charcoal text-sm font-medium">Months Payback</div>
              </div>
              <div className="bg-white/90 rounded-xl p-6 text-center">
                <div className="text-navy text-3xl font-bold mb-2">350%+</div>
                <div className="text-charcoal text-sm font-medium">3-Year ROI</div>
              </div>
            </div>
            <p className="text-navy/70 text-sm text-center">
              Savings from reduced expiry losses, faster compliance, improved distributor management, and operational efficiency.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}